import { createReactClient } from '@gqty/react'
import { Cache, createClient, type QueryFetcher, type CacheNode } from 'gqty'
import { GraphQLError } from 'graphql'
import { generatedSchema, scalarsEnumsHash, type GeneratedSchema } from './schema.generated'
import { gqlMutate } from '../requests.service.js'

// ---------------------------------------------------------------------------
// Error reporter — registered by the app on startup (e.g. web/core/index.jsx)
// Kept as a simple callback so shared/ doesn't depend on any app-specific code.
// ---------------------------------------------------------------------------
type GQtyErrorReporter = (error: Error, context: { operation: string; code: string }) => void
let _errorReporter: GQtyErrorReporter | null = null

export function setGQtyErrorReporter(reporter: GQtyErrorReporter): void {
  _errorReporter = reporter
}

/** Extract the first operation name from a GQL query string (e.g. "query GetSite" → "GetSite") */
function extractOperation(query: string): string {
  const match = query.match(/(?:query|mutation|subscription)\s+(\w+)/i)
  return match?.[1] ?? 'UnknownOperation'
}

/** Returns true for errors that are expected and not worth alerting on */
function isSilentError(message: string, code: string): boolean {
  const msg = message.toLowerCase()
  return (
    code === 'UNAUTHENTICATED' ||
    code === 'CIRCUIT_OPEN' ||
    msg.includes('401') ||
    msg.includes('unauthorized') ||
    msg.includes('unauthenticated') ||
    msg === 'failed to fetch' ||
    msg.includes('not found') ||
    msg.includes('does not exist') ||
    msg.includes('service temporarily unavailable')
  )
}

// Dedup: don't report the same error more than once per 30s per operation.
// GQty re-fetches on every re-render when cache is empty, so without this a
// single broken backend query can generate thousands of fe-errors calls.
const _recentGQtyErrors = new Map<string, number>()
const GQTY_ERROR_DEDUP_MS = 30_000

function shouldReportGQtyError(operation: string, message: string): boolean {
  const key = `${operation}:${message}`
  const now = Date.now()
  const last = _recentGQtyErrors.get(key)
  if (last !== undefined && now - last < GQTY_ERROR_DEDUP_MS) {
    return false
  }
  _recentGQtyErrors.set(key, now)
  // Prevent unbounded growth
  if (_recentGQtyErrors.size > 100) {
    const cutoff = now - GQTY_ERROR_DEDUP_MS
    for (const [k, t] of _recentGQtyErrors) {
      if (t < cutoff) {
        _recentGQtyErrors.delete(k)
      }
    }
  }
  return true
}

const queryFetcher: QueryFetcher = async function ({ query, variables }) {
  const operation = extractOperation(query as string)

  try {
    const result = await gqlMutate({
      mutation: query as any,
      variables,
      options: {
        isV1: false,
      },
    })

    return {
      data: result as Record<string, unknown>,
    }
  } catch (error: any) {
    if (error?.response?.errors && Array.isArray(error.response.errors)) {
      const gqlErrors = error.response.errors.map(
        (gqlError: any) =>
          new GraphQLError(
            gqlError.extensions?.userMessage || gqlError.message || 'An error occurred',
            {
              extensions: {
                code: gqlError.extensions?.code || 'UNKNOWN_ERROR',
                originalMessage: gqlError.message,
                ...gqlError.extensions,
              },
            }
          )
      )

      // Report actionable errors to the registered observer
      if (_errorReporter) {
        for (const gqlError of gqlErrors) {
          const code = String((gqlError.extensions?.code as string) ?? 'UNKNOWN_ERROR')
          if (
            !isSilentError(gqlError.message, code) &&
            shouldReportGQtyError(operation, gqlError.message)
          ) {
            const err = new Error(gqlError.message)
            err.name = `GQtyError`
            _errorReporter(err, { operation, code })
          }
        }
      }

      return {
        data: (error.response.data || null) as Record<string, unknown> | null,
        errors: gqlErrors,
      }
    }

    // Network-level error
    const networkMessage = error?.message || 'Request failed'
    if (
      _errorReporter &&
      !isSilentError(networkMessage, error?.code ?? '') &&
      shouldReportGQtyError(operation, networkMessage)
    ) {
      const err = new Error(networkMessage)
      err.name = 'GQtyNetworkError'
      _errorReporter(err, { operation, code: 'NETWORK_ERROR' })
    }

    return {
      data: null as null,
      errors: [new GraphQLError(networkMessage)],
    }
  }
}

const NON_NORMALIZED_TYPES = new Set([
  'CoreUsersWithoutPayrollTier',
  'Invoice',
  'InvoiceRow',
  'Invoices',
  'InvoicesPageInfo',
  'InvoicesResults',
  'Session',
  'SessionWaitlistEntry',
  // Content Suite pagination types — cursors are UUIDs, not base64;
  // normalizing these can corrupt the page-info cache and break pagination.
  'ContentEntries',
  'ContentEntriesPageInfo',
  'ContentEntriesResults',
])

const cache = new Cache(undefined, {
  maxAge: Infinity, // Keep cache entries indefinitely for normalized cache
  normalization: {
    identity: (value: CacheNode) => {
      // Handle entities that follow the {entityName}Id pattern
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const obj = value as Record<string, unknown>
        const typename = obj.__typename as string

        if (typename) {
          // Skip normalization for pagination result types
          if (NON_NORMALIZED_TYPES.has(typename)) {
            return undefined
          }

          // Try {typename}Id pattern — lowercase first char to preserve camelCase
          // e.g. ServiceTemplate -> serviceTemplateId, Site -> siteId
          const entityIdField = `${typename.charAt(0).toLowerCase()}${typename.slice(1)}Id`
          if (obj[entityIdField]) {
            return `${typename}:${obj[entityIdField]}`
          }

          // Fallback: full lowercase for acronym-prefixed types (e.g. AIThing -> aithingId)
          const entityIdFieldLower = `${typename.toLowerCase()}Id`
          if (entityIdFieldLower !== entityIdField && obj[entityIdFieldLower]) {
            return `${typename}:${obj[entityIdFieldLower]}`
          }

          // Fallback to generic 'id' field for entities that don't follow the pattern
          if (obj.id) {
            return `${typename}:${obj.id}`
          }
        }
      }
      return undefined
    },
  },
})

export const client = createClient<GeneratedSchema>({
  schema: generatedSchema,
  scalars: scalarsEnumsHash,
  cache,
  fetchOptions: {
    fetcher: queryFetcher,
  },
})

export const { resolve, subscribe, schema } = client

export { cache }

export const { query, mutation, mutate, subscription, resolved, refetch, track } = client

export const {
  graphql,
  useQuery,
  usePaginatedQuery,
  useTransactionQuery,
  useLazyQuery,
  useRefetch,
  useMutation,
  useMetaState,
  prepareReactRender,
  useHydrateCache,
  prepareQuery,
} = createReactClient<GeneratedSchema>(client, {
  defaults: {
    suspense: false,
  },
})

export * from './schema.generated'

export interface MutationOptions<TData = any, TError = any> {
  onSuccess?: (data: TData) => void
  onError?: (error: TError) => void
}

// GQtyError exposes the underlying GraphQL errors on `graphQLErrors[]`, not as
// top-level `extensions` — check both shapes so direct GraphQLError throws and
// GQty-wrapped ones both surface their `userMessage`.
type ErrorDetail = { field?: string; reason?: string; holders?: unknown }

type ErrorExtensions = {
  userMessage?: string
  code?: string
  details?: ReadonlyArray<ErrorDetail>
}

type WrappedGraphQLError = {
  message?: string
  extensions?: ErrorExtensions
}

type ErrorWithExtensions = Error & {
  extensions?: ErrorExtensions
  graphQLErrors?: ReadonlyArray<WrappedGraphQLError>
  response?: { errors?: ReadonlyArray<WrappedGraphQLError> }
}

export function getErrorMessage(error: ErrorWithExtensions, fallback?: string) {
  const wrapped = error?.graphQLErrors?.[0] ?? error?.response?.errors?.[0]
  const userMessage = error?.extensions?.userMessage || wrapped?.extensions?.userMessage
  return userMessage || wrapped?.message || error?.message || fallback || 'Please try again'
}

export function getErrorReason(error: ErrorWithExtensions): string | undefined {
  return getErrorDetail(error)?.reason
}

/**
 * The full first detail a ValidationException attached, for consumers that need more
 * than the reason — e.g. the fob collision detail carries the holding members so the
 * dialog can link to their profiles.
 */
export function getErrorDetail(error: ErrorWithExtensions): ErrorDetail | undefined {
  const details = error?.extensions?.details || error?.graphQLErrors?.[0]?.extensions?.details
  return details?.[0]
}
